import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  changePassword,
  createCategory,
  createSupplier,
  deleteCategory,
  deleteSupplier,
  getCategories,
  getSettings,
  getSuppliers,
  printJob,
  saveSettings,
  testPrinterConnection,
  updateCategory,
  updateSupplier,
} from '../utils/api';
import { openReceiptPreview } from '../utils/receipt';

const SAMPLE_RECEIPT = {
  receipt_no: 'PREVIEW-001',
  created_at: new Date().toISOString(),
  payment_method: 'cash',
  subtotal: 580,
  tax_amt: 92.8,
  total: 672.8,
  items: [
    { name: 'Coffee Beans 500g', qty: 1, lineTotal: 380 },
    { name: 'Fresh Milk 1L', qty: 2, lineTotal: 200 },
  ],
};

function FormField({ label, children }) {
  return <div className="form-group"><label className="form-label">{label}</label>{children}</div>;
}

function InlineEditor({ title, items, emptyLabel, fields, onCreate, onUpdate, onDelete, newItem, setNewItem, saving }) {
  return (
    <div className="card card-pad mb-16">
      <div className="card-title" style={{ marginBottom: 16 }}>{title}</div>
      <div style={{ display: 'grid', gap: 12 }}>
        {items.length === 0 ? (
          <div className="text-sm text-muted">{emptyLabel}</div>
        ) : items.map((item) => (
          <div key={item.id} className="card" style={{ padding: 14, borderRadius: 'var(--r)' }}>
            <div className="form-grid-2">
              {fields.map((field) => (
                <FormField key={field.key} label={field.label}>
                  <input className="form-input" value={item[field.key] || ''} onChange={(event) => onUpdate(item.id, { [field.key]: event.target.value })} placeholder={field.placeholder || ''} />
                </FormField>
              ))}
            </div>
            <div className="inventory-row-actions">
              <button className="btn btn-danger btn-sm" onClick={() => onDelete(item.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      <div className="divider" style={{ margin: '16px 0' }} />
      <div className="form-grid-2">
        {fields.map((field) => (
          <FormField key={field.key} label={`New ${field.label}`}>
            <input className="form-input" value={newItem[field.key] || ''} onChange={(event) => setNewItem((value) => ({ ...value, [field.key]: event.target.value }))} placeholder={field.placeholder || ''} />
          </FormField>
        ))}
      </div>
      <button className="btn btn-primary" onClick={onCreate} disabled={saving}>Add {title.slice(0, -1)}</button>
    </div>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [saved, setSaved] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [newCategory, setNewCategory] = useState({ name: '', color: '#4b79e8', icon: 'PKG' });
  const [newSupplier, setNewSupplier] = useState({ name: '', contact: '', phone: '', email: '', address: '' });

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => getSettings().then((response) => response.data) });
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => getCategories().then((response) => response.data) });
  const { data: suppliers = [] } = useQuery({ queryKey: ['suppliers'], queryFn: () => getSuppliers().then((response) => response.data) });

  useEffect(() => {
    if (settings) {
      setForm((prev) => ({
        printer_model: 'Epson TM-T20III',
        printer_connection: 'network',
        printer_host: '',
        printer_port: '9100',
        ...settings,
        ...prev,
      }));
    }
  }, [settings]);

  const saveMut = useMutation({
    mutationFn: (payload) => saveSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      toast.success('Settings saved');
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Save failed'),
  });

  const passwordMut = useMutation({
    mutationFn: ({ currentPassword, newPassword }) => changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Password update failed'),
  });

  const testPrintMut = useMutation({
    mutationFn: () => printJob('receipt', { ...SAMPLE_RECEIPT, settingsSnapshot: form }, navigator.userAgent.slice(0, 80)),
    onSuccess: () => toast.success('Test receipt queued for printing'),
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to queue print job'),
  });

  const printerCheckMut = useMutation({
    mutationFn: () => testPrinterConnection(form.printer_host, form.printer_port),
    onSuccess: (response) => toast.success(response.data?.message || 'Printer is reachable'),
    onError: (err) => toast.error(err?.response?.data?.error || 'Printer connection failed'),
  });

  const categoryCreateMut = useMutation({ mutationFn: () => createCategory(newCategory), onSuccess: () => { qc.invalidateQueries({ queryKey: ['categories'] }); setNewCategory({ name: '', color: '#4b79e8', icon: 'PKG' }); toast.success('Category added'); }, onError: (err) => toast.error(err?.response?.data?.error || 'Unable to add category') });
  const categoryUpdateMut = useMutation({ mutationFn: ({ id, payload }) => updateCategory(id, payload), onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }), onError: (err) => toast.error(err?.response?.data?.error || 'Unable to update category') });
  const categoryDeleteMut = useMutation({ mutationFn: (id) => deleteCategory(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }), onError: (err) => toast.error(err?.response?.data?.error || 'Unable to delete category') });

  const supplierCreateMut = useMutation({ mutationFn: () => createSupplier(newSupplier), onSuccess: () => { qc.invalidateQueries({ queryKey: ['suppliers'] }); setNewSupplier({ name: '', contact: '', phone: '', email: '', address: '' }); toast.success('Supplier added'); }, onError: (err) => toast.error(err?.response?.data?.error || 'Unable to add supplier') });
  const supplierUpdateMut = useMutation({ mutationFn: ({ id, payload }) => updateSupplier(id, payload), onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }), onError: (err) => toast.error(err?.response?.data?.error || 'Unable to update supplier') });
  const supplierDeleteMut = useMutation({ mutationFn: (id) => deleteSupplier(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }), onError: (err) => toast.error(err?.response?.data?.error || 'Unable to delete supplier') });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    saveMut.mutate(form);
  }

  function handlePasswordUpdate() {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      toast.error('Enter your current and new password');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    passwordMut.mutate(passwordForm);
  }

  function previewReceipt() {
    openReceiptPreview({ ...SAMPLE_RECEIPT, settingsSnapshot: form }, form);
  }

  return (
    <div className="p-page fade-up">
      <div className="grid-2">
        <div className="card card-pad mb-16">
          <div className="card-title" style={{ marginBottom: 16 }}>Store Details</div>
          <FormField label="Store Name"><input className="form-input" value={form.store_name || ''} onChange={(event) => setField('store_name', event.target.value)} placeholder="Your Store" /></FormField>
          <FormField label="Address"><input className="form-input" value={form.store_address || ''} onChange={(event) => setField('store_address', event.target.value)} placeholder="Enter store address" /></FormField>
          <FormField label="Phone Number"><input className="form-input" value={form.store_phone || ''} onChange={(event) => setField('store_phone', event.target.value)} placeholder="+254 700 000 000" /></FormField>
          <FormField label="Currency">
            <select className="form-input form-select" value={form.currency || 'KES'} onChange={(event) => setField('currency', event.target.value)}>
              <option value="KES">KES - Kenyan Shilling</option>
              <option value="USD">USD - US Dollar</option>
              <option value="TZS">TZS - Tanzanian Shilling</option>
              <option value="UGX">UGX - Ugandan Shilling</option>
            </select>
          </FormField>
          <FormField label="Default VAT Rate (%)"><input className="form-input" type="number" value={form.tax_rate || ''} onChange={(event) => setField('tax_rate', event.target.value)} /></FormField>
          <FormField label="Receipt Footer"><textarea className="form-input" rows={3} style={{ resize: 'vertical' }} value={form.receipt_footer || ''} onChange={(event) => setField('receipt_footer', event.target.value)} /></FormField>
        </div>

        <div className="card card-pad mb-16">
          <div className="card-title" style={{ marginBottom: 16 }}>Printer & Receipt</div>
          <FormField label="Printer Model"><input className="form-input" value={form.printer_model || ''} onChange={(event) => setField('printer_model', event.target.value)} placeholder="Epson TM-T20III" /></FormField>
          <FormField label="Printer IP Address"><input className="form-input" value={form.printer_host || ''} onChange={(event) => setField('printer_host', event.target.value)} placeholder="192.168.1.100" /></FormField>
          <FormField label="Printer Port"><input className="form-input" value={form.printer_port || ''} onChange={(event) => setField('printer_port', event.target.value)} placeholder="9100" /></FormField>
          <FormField label="Connection Type">
            <select className="form-input form-select" value={form.printer_connection || 'network'} onChange={(event) => setField('printer_connection', event.target.value)}>
              <option value="network">Network (TCP/IP)</option>
              <option value="usb">USB</option>
              <option value="bluetooth">Bluetooth</option>
            </select>
          </FormField>
          <div className="flex gap-8 mt-12" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={previewReceipt}>Preview Receipt / Save PDF</button>
            <button className="btn btn-secondary" onClick={() => testPrintMut.mutate()} disabled={testPrintMut.isPending}>Queue Test Print</button>
            <button className="btn btn-primary" onClick={() => printerCheckMut.mutate()} disabled={printerCheckMut.isPending || !form.printer_host || !form.printer_port}>Test Printer Connection</button>
          </div>
        </div>

        <InlineEditor
          title="Categories"
          items={categories}
          emptyLabel="No categories yet."
          fields={[
            { key: 'name', label: 'Name', placeholder: 'Groceries' },
            { key: 'icon', label: 'Icon', placeholder: 'PKG' },
            { key: 'color', label: 'Color', placeholder: '#4b79e8' },
          ]}
          newItem={newCategory}
          setNewItem={setNewCategory}
          onCreate={() => categoryCreateMut.mutate()}
          onUpdate={(id, payload) => categoryUpdateMut.mutate({ id, payload })}
          onDelete={(id) => categoryDeleteMut.mutate(id)}
          saving={categoryCreateMut.isPending}
        />

        <InlineEditor
          title="Suppliers"
          items={suppliers}
          emptyLabel="No suppliers yet."
          fields={[
            { key: 'name', label: 'Name', placeholder: 'Acme Supplies' },
            { key: 'contact', label: 'Contact', placeholder: 'Jane' },
            { key: 'phone', label: 'Phone', placeholder: '+254700000000' },
            { key: 'email', label: 'Email', placeholder: 'orders@example.com' },
            { key: 'address', label: 'Address', placeholder: 'Nairobi' },
          ]}
          newItem={newSupplier}
          setNewItem={setNewSupplier}
          onCreate={() => supplierCreateMut.mutate()}
          onUpdate={(id, payload) => supplierUpdateMut.mutate({ id, payload })}
          onDelete={(id) => supplierDeleteMut.mutate(id)}
          saving={supplierCreateMut.isPending}
        />

        <div className="card card-pad mb-16">
          <div className="card-title" style={{ marginBottom: 16 }}>System</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => qc.invalidateQueries({ queryKey: ['settings'] })}>Reload Settings</button>
            <button className="btn btn-secondary" onClick={() => { setForm(settings || {}); toast.success('Unsaved changes cleared'); }}>Reset Unsaved Changes</button>
            <button className="btn btn-secondary" onClick={() => { localStorage.removeItem('daima_auth'); localStorage.removeItem('daima_token'); toast.success('Local device auth cache cleared'); }}>Clear Device Cache</button>
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-title" style={{ marginBottom: 16 }}>Change Password</div>
          <FormField label="Current Password"><input className="form-input" type="password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))} placeholder="Current password" /></FormField>
          <FormField label="New Password"><input className="form-input" type="password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))} placeholder="Min 6 characters" /></FormField>
          <FormField label="Confirm New Password"><input className="form-input" type="password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))} placeholder="Repeat new password" /></FormField>
          <button className="btn btn-primary" onClick={handlePasswordUpdate} disabled={passwordMut.isPending}>{passwordMut.isPending ? 'Updating...' : 'Update Password'}</button>
        </div>
      </div>

      <div className="sticky-save-bar">
        <button className="btn btn-primary" onClick={handleSave} disabled={saveMut.isPending}>
          {saveMut.isPending ? 'Saving...' : saved ? 'Saved!' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
}
